import { useCallback, useEffect, useState } from "react";
import { Trash2, UserPlus } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { NativeSelect } from "@/components/NativeSelect";
import { Switch } from "@/components/ui/switch";
import { useAuth } from "@/context/AuthContext";
import { api, errText } from "@/lib/api";
import { GRADES, ROLE_NAMES } from "@/lib/constants";

function CreateUser({ onCreated }) {
  const empty = { username: "", password: "", role: "user", grade: "" };
  const [f, setF] = useState(empty);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/users", { ...f, grade: f.grade ? Number(f.grade) : null });
      toast.success(`Пользователь ${f.username} создан`); setF(empty); onCreated();
    } catch (err) { toast.error(errText(err)); }
  };
  return (
    <form onSubmit={submit} className="rounded-2xl bg-white border border-[#d1e7dd] p-5 grid sm:grid-cols-2 lg:grid-cols-5 gap-3 items-end" data-testid="create-user-form">
      <Input required placeholder="Юзернейм" value={f.username} onChange={set("username")} data-testid="create-user-username-input" />
      <Input required placeholder="Пароль (мин. 6)" value={f.password} onChange={set("password")} data-testid="create-user-password-input" />
      <NativeSelect value={f.role} onChange={set("role")} data-testid="create-user-role-select">
        {Object.entries(ROLE_NAMES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
      </NativeSelect>
      <NativeSelect value={f.grade} onChange={set("grade")} data-testid="create-user-grade-select">
        <option value="">Класс — выберет сам</option>
        {GRADES.map((g) => <option key={g} value={g}>{g} класс</option>)}
      </NativeSelect>
      <Button type="submit" className="rounded-full bg-[#1f5f42] hover:bg-[#174731] h-10" data-testid="create-user-submit-button"><UserPlus className="h-4 w-4 mr-1" />Создать</Button>
    </form>
  );
}

function RegistrationToggle() {
  const [open, setOpen] = useState(null);
  useEffect(() => { api.get("/settings/public").then((r) => setOpen(r.data.registration_open)).catch(() => {}); }, []);
  const toggle = async (v) => {
    try {
      const { data } = await api.put("/admin/settings", { registration_open: v });
      setOpen(data.registration_open);
      toast.success(v ? "Регистрация открыта" : "Регистрация закрыта");
    } catch (e) { toast.error(errText(e)); }
  };
  return (
    <div className="rounded-2xl bg-white border border-[#d1e7dd] p-5 flex items-center justify-between gap-4">
      <div>
        <div className="font-display font-bold text-[#0b2e20]">Открытая регистрация</div>
        <div className="text-sm text-[#4b6b5c]">{open ? "Любой может создать аккаунт" : "Аккаунты создаёт только администратор"}</div>
      </div>
      <Switch checked={!!open} disabled={open === null} onCheckedChange={toggle} data-testid="registration-toggle" />
    </div>
  );
}

export default function AdminUsers({ onChange }) {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState("");
  const load = useCallback(() => { api.get("/admin/users").then((r) => setUsers(r.data)).catch(() => {}); onChange?.(); }, [onChange]);
  useEffect(() => { load(); }, [load]);

  const setRole = async (u, role) => {
    try { await api.patch(`/admin/users/${u.id}/role`, { role }); toast.success("Роль изменена"); load(); }
    catch (e) { toast.error(errText(e)); }
  };
  const remove = async (u) => {
    if (!window.confirm(`Удалить ${u.username}?`)) return;
    try { await api.delete(`/admin/users/${u.id}`); toast.success("Удалён"); load(); }
    catch (e) { toast.error(errText(e)); }
  };
  const shown = users.filter((u) => u.username.toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="space-y-5">
      <RegistrationToggle />
      <CreateUser onCreated={load} />
      <Input placeholder="Поиск по юзернейму" value={q} onChange={(e) => setQ(e.target.value)} className="max-w-xs bg-white" data-testid="admin-users-search-input" />
      <div className="rounded-2xl bg-white border border-[#d1e7dd] overflow-x-auto">
        <table className="w-full text-sm" data-testid="admin-users-table">
          <thead><tr className="text-left text-xs uppercase tracking-wider text-[#2e7d56] border-b border-[#d1e7dd]">
            <th className="p-4">Юзернейм</th><th className="p-4">Класс</th><th className="p-4">Коины</th><th className="p-4">Файлов</th><th className="p-4">Роль</th><th className="p-4" />
          </tr></thead>
          <tbody>
            {shown.map((u) => (
              <tr key={u.id} className="border-b border-[#eef6f1] last:border-0" data-testid={`admin-user-row-${u.id}`}>
                <td className="p-4 font-semibold text-[#0b2e20]">{u.username}</td>
                <td className="p-4">{u.grade || "—"}</td>
                <td className="p-4 font-semibold text-amber-700">{u.role === "admin" ? "∞" : u.coins}</td>
                <td className="p-4">{u.approved_files}</td>
                <td className="p-4">
                  {u.id === me.id ? <span className="font-semibold">{ROLE_NAMES[u.role]}</span> : (
                    <NativeSelect value={u.role} onChange={(e) => setRole(u, e.target.value)} className="h-9 w-36" data-testid={`user-role-select-${u.id}`}>
                      {Object.entries(ROLE_NAMES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                    </NativeSelect>
                  )}
                </td>
                <td className="p-4 text-right">
                  {u.id !== me.id && <button onClick={() => remove(u)} className="h-8 w-8 grid place-items-center rounded-full text-red-600 hover:bg-red-50" data-testid={`delete-user-${u.id}`}><Trash2 className="h-4 w-4" /></button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
